/**
 * SimplyLoan GEO Profit Optimizer / Autonomous Marketing Agent API Client.
 *
 * Connects the Afima.ai trading desk to `/api/simplyloan/geo-optimizer`, supporting Norway (`no`)
 * primary focus (plus `se` and `fi`) across Google Ads, Microsoft Advertising, and Meta Ads, including the
 * monthly P&L command center (60K budget / 20K max loss), lender EPC & funnel routing, and the 3-tier
 * autonomous loop (intraday guardian, daily solver, creative strategist).
 */
import { API_BASE_URL } from '../config';

export type OptimizerGeo = 'no' | 'se' | 'fi';
export type OptimizerPlatform = 'google' | 'microsoft' | 'meta';
export type OptimizerMode = 'recommend_only' | 'auto_hygiene' | 'autopilot_bounded' | 'full_autopilot';
export type OptimizerTier = 'intraday_guardian' | 'daily_solver' | 'creative_strategist';
export type PacingRegime = 'normal_scale' | 'pro_rata_pace' | 'loss_buffer_throttle' | 'survival_floor';
export type ProposalType =
  | 'budget_shift'
  | 'negative_keyword'
  | 'keyword_promotion'
  | 'pause_bleeder'
  | 'enable_winner'
  | 'new_rsa'
  | 'bid_update'
  | 'landing_url'
  | 'campaign_create'
  | 'ad_pause'
  | 'emergency_throttle'
  | 'meta_creative';

export interface GeoOptimizerSettings {
  geo: OptimizerGeo;
  daily_budget_cap_dkk: number;
  target_roas_d30: number;
  target_cpl_dkk: number;
  max_daily_step_pct: number;
  min_campaign_daily_budget_dkk: number;
  wasted_term_spend_dkk: number;
  wasted_term_clicks: number;
  winner_term_min_conversions: number;
  bleeder_spend_dkk: number;
  mode: OptimizerMode;
  channel_weights: Record<OptimizerPlatform, number>;
  notes?: string;
  updated_by?: string;
  updated_at?: string;
  monthly_budget_cap_dkk: number;
  monthly_max_loss_dkk: number;
  min_daily_floor_dkk: number;
  max_daily_cap_dkk: number;
}

export interface GeoSpecialistProfile {
  geo: OptimizerGeo;
  country_name: string;
  flag: string;
  domain: string;
  legal_regime: string;
  credit_registry: string;
  representative_example: string;
  off_intent_categories: string[];
  high_intent_clusters: string[];
  landing_paths?: string[];
  lender_slugs?: string[];
  brand_colors?: Record<string, string>;
}

export interface PortfolioCampaignRow {
  platform: OptimizerPlatform;
  campaign_id: string;
  campaign_name: string;
  status: string;
  bidding_strategy: string;
  current_daily_budget_dkk: number;
  recommended_daily_budget_dkk: number;
  budget_delta_dkk: number;
  budget_delta_pct: number;
  min_daily_budget_dkk: number;
  max_daily_budget_dkk: number;
  spend_30d_dkk: number;
  clicks_30d: number;
  impressions_30d: number;
  leads_30d: number;
  rev_d0_dkk: number;
  rev_d30_dkk: number;
  projected_d30_dkk: number;
  observed_roas_d30: number;
  bayesian_rpl_d30_dkk: number;
  bayesian_cpl_dkk: number;
  bayesian_roas_d30: number;
  marginal_roas_d30: number;
  headroom_label: string;
  impression_share?: number;
  lost_is_budget?: number;
  expected_daily_margin_lift_dkk: number;
  is_reference_data?: boolean;
}

export interface ChannelTotalSummary {
  current_daily_dkk: number;
  recommended_daily_dkk: number;
  spend_30d_dkk: number;
  rev_d30_dkk: number;
  leads_30d: number;
}

export interface PortfolioSnapshot {
  geo: OptimizerGeo;
  country_name: string;
  domain: string;
  legal_regime: string;
  budget_cap_dkk: number;
  current_spend_daily_dkk: number;
  recommended_spend_daily_dkk: number;
  current_projected_d30_roas: number;
  optimized_projected_d30_roas: number;
  expected_daily_margin_lift_dkk: number;
  channel_totals: Record<OptimizerPlatform, ChannelTotalSummary>;
  campaigns: PortfolioCampaignRow[];
  tier?: string;
  pnl?: Partial<MonthlyPnlSnapshot>;
}

export interface OptimizerProposal {
  id: number;
  run_id?: number;
  geo: OptimizerGeo;
  platform: OptimizerPlatform;
  proposal_type: ProposalType;
  priority: 'critical' | 'high' | 'medium' | 'low';
  target_object_type: string;
  target_object_id: string;
  target_object_name: string;
  title: string;
  rationale: string;
  expected_daily_margin_impact_dkk: number;
  confidence: number;
  is_safe_hygiene: boolean;
  action_payload: Record<string, any>;
  rollback_payload: Record<string, any>;
  status: 'pending' | 'applied' | 'rejected' | 'rolled_back' | 'failed';
  decided_by?: string | null;
  decided_at?: string | null;
  execution_result?: Record<string, any> | null;
  created_at?: string;
}

export interface OptimizerRunSummary {
  id: number;
  geo?: string;
  run_mode: OptimizerMode;
  budget_cap_dkk?: number;
  current_spend_daily_dkk?: number;
  recommended_spend_daily_dkk?: number;
  current_projected_d30_roas?: number;
  optimized_projected_d30_roas?: number;
  expected_daily_margin_lift_dkk?: number;
  proposals_count?: number;
  auto_applied_count?: number;
  summary: string;
  triggered_by?: string;
  created_at: string;
}

export interface PnlAlert {
  level: 'critical' | 'warning' | 'info';
  code: string;
  message: string;
}

export interface AdvertiserAlpha {
  advertiser: string;
  approved_90d: number;
  declined_90d: number;
  alpha: number;
  shrunk: boolean;
}

export interface RevenueBreakdown {
  paid_d0_revenue_dkk: number;
  email_drip_revenue_dkk: number;
  organic_seo_revenue_dkk: number;
  other_market_revenue_dkk?: number;
  approved_revenue_dkk: number;
  risk_adjusted_pending_revenue_dkk: number;
  by_platform?: Record<string, number>;
  conversions_approved?: number;
  conversions_pending?: number;
}

export interface MonthlyPnlSnapshot {
  geo: OptimizerGeo;
  month_key: string;
  timezone?: string;
  as_of?: string;
  today?: string;
  days_in_month?: number;
  day_of_month?: number;
  days_remaining: number;
  monthly_budget_cap_dkk: number;
  monthly_max_loss_dkk: number;
  min_daily_floor_dkk: number;
  max_daily_cap_dkk: number;
  mtd_spend_google_dkk: number;
  mtd_spend_microsoft_dkk: number;
  mtd_spend_meta_dkk: number;
  mtd_spend_total_dkk: number;
  today_spend_dkk?: number;
  mtd_rev_approved_dkk: number;
  mtd_rev_pending_raw_dkk: number;
  mtd_rev_pending_risk_adj_dkk: number;
  mtd_rev_total_risk_adj_dkk: number;
  revenue_breakdown?: RevenueBreakdown;
  advertiser_alphas?: AdvertiserAlpha[];
  alpha_overall?: number;
  mtd_net_loss_dkk: number;
  remaining_budget_dkk: number;
  remaining_loss_buffer_dkk: number;
  trailing_roas_14d?: number;
  today_allowed_daily_budget_dkk: number;
  pacing_regime: PacingRegime;
  exploration_floor_dkk?: number;
  projected_eom_spend_dkk?: number;
  projected_eom_net_loss_dkk?: number;
  alerts?: PnlAlert[];
  warnings?: string[];
  snapshot_kind?: string;
  created_at?: string;
}

export interface TrackingHealth {
  insights_stale: boolean;
  stale_platforms: string[];
  synced_at: Record<string, string | null>;
  tracking_outage: boolean;
  clicks_12h: number | null;
  clicks_prior_7d: number | null;
  spend_12h_proxy_dkk: number | null;
  freeze: boolean;
  alerts: PnlAlert[];
}

export interface LenderEpc {
  slug: string;
  advertiser?: string | null;
  clicks: number;
  unique_leads: number;
  approved: number;
  pending: number;
  declined: number;
  approved_dkk: number;
  pending_raw_dkk: number;
  pending_risk_adj_dkk: number;
  revenue_risk_adj_dkk: number;
  epc_dkk: number;
  conversion_rate: number;
  approval_rate_alpha: number;
  alpha_shrunk: boolean;
  avg_payout_dkk?: number | null;
}

export interface LandingCandidate {
  path: string;
  url: string;
  bayesian_rpl_d30_dkk: number;
  leads: number;
  clickout_rate?: number | null;
  weight: number;
}

export interface LandingRoute {
  cluster: string;
  chosen_path: string;
  chosen_url: string;
  chosen_rpl_d30_dkk: number;
  evidence_leads: number;
  reroute_allowed: boolean;
  candidates: LandingCandidate[];
}

export interface LandingPathPerformance {
  path: string;
  leads: number;
  clickouts: number;
  rev_d30_dkk: number;
  conversions: number;
  bayesian_rpl_d30_dkk: number;
  clickout_rate: number;
  conversion_rate: number;
  destination: 'website' | 'instant_form';
}

export interface ValueCalibration {
  lead_value_dkk: Record<OptimizerPlatform, number>;
  clickout_epc_dkk: Record<string, number>;
  purchase_rule: string;
  computed_at: string;
  persisted_rows?: number;
}

export interface FunnelSnapshot {
  geo: OptimizerGeo;
  lenders_30d: LenderEpc[];
  lenders_90d: LenderEpc[];
  hooks?: Record<string, any>;
  routing: { clusters: LandingRoute[]; paths: LandingPathPerformance[]; default_url: string };
  calibration: ValueCalibration;
}

export interface TierRunInfo {
  run_id: number;
  created_at: string;
  summary?: string;
  auto_applied_count?: number;
  proposals_count?: number;
}

export interface AutonomyInfo {
  tiers: Partial<Record<OptimizerTier, TierRunInfo>>;
  modes: OptimizerMode[];
  principal: string;
}

export interface GeoOptimizerDashboardResponse {
  geo: OptimizerGeo;
  supported_geos: OptimizerGeo[];
  profile: GeoSpecialistProfile;
  settings: GeoOptimizerSettings;
  portfolio: PortfolioSnapshot;
  proposals: OptimizerProposal[];
  latest_run: OptimizerRunSummary | null;
  recent_runs: OptimizerRunSummary[];
  pnl?: MonthlyPnlSnapshot | null;
  health?: TrackingHealth | null;
  funnel?: FunnelSnapshot | null;
  autonomy?: AutonomyInfo;
}

export interface TierRunResult {
  status: string;
  geo: OptimizerGeo;
  tier: OptimizerTier;
  mode: OptimizerMode;
  run_id: number | null;
  summary: string;
  auto_applied_count: number;
  blocked_count: number;
  proposals: OptimizerProposal[];
  pnl?: MonthlyPnlSnapshot;
  health?: TrackingHealth;
  [key: string]: any;
}

const BASE = `${API_BASE_URL}/api/simplyloan/geo-optimizer`;

async function requestJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      data.reason ||
        data.execution_result?.reason ||
        data.error ||
        `HTTP ${response.status}`
    );
  }
  return data as T;
}

export async function fetchGeoOptimizerDashboard(
  geo: OptimizerGeo = 'no',
  windowDays: number = 30,
  options?: { includeFunnel?: boolean }
): Promise<GeoOptimizerDashboardResponse> {
  const includeFunnel = options?.includeFunnel ?? windowDays > 3;
  const qs = new URLSearchParams({
    geo,
    window_days: String(windowDays),
    include_funnel: includeFunnel ? '1' : '0',
  });
  return requestJson<GeoOptimizerDashboardResponse>(`${BASE}?${qs.toString()}`);
}

export async function evaluateRolling72h(
  geo: OptimizerGeo = 'se',
  sendEmail: boolean = false
): Promise<{
  status: string;
  run_id: number;
  zone: string;
  zone_label: string;
  summary: string;
  rolling_72h: Record<string, any>;
  proposals: OptimizerProposal[];
}> {
  return requestJson(`${BASE}/evaluate-72h`, {
    method: 'POST',
    body: JSON.stringify({ geo, send_email: sendEmail }),
  });
}

export async function fetchMonthlyPnl(geo: OptimizerGeo = 'no'): Promise<{
  status: string;
  geo: OptimizerGeo;
  pnl: MonthlyPnlSnapshot;
  health: TrackingHealth;
}> {
  const qs = new URLSearchParams({ geo });
  return requestJson(`${BASE}/pnl?${qs.toString()}`);
}

export async function fetchFunnel(geo: OptimizerGeo = 'no'): Promise<{ status: string; geo: OptimizerGeo; funnel: FunnelSnapshot }> {
  const qs = new URLSearchParams({ geo });
  return requestJson(`${BASE}/funnel?${qs.toString()}`);
}

export async function updateGeoOptimizerSettings(
  payload: Partial<GeoOptimizerSettings> & { geo: OptimizerGeo }
): Promise<{ status: string; settings: GeoOptimizerSettings }> {
  return requestJson<{ status: string; settings: GeoOptimizerSettings }>(`${BASE}/settings`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function triggerGeoOptimizerRun(params: {
  geo: OptimizerGeo;
  window_days?: number;
  daily_budget_cap_dkk?: number;
}): Promise<{
  status: string;
  run_id: number;
  summary: string;
  auto_applied_count: number;
  blocked_count?: number;
  portfolio: PortfolioSnapshot;
  proposals: OptimizerProposal[];
  pnl?: MonthlyPnlSnapshot;
  health?: TrackingHealth;
}> {
  return requestJson(`${BASE}/run`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

const TIER_PATHS: Record<OptimizerTier, string> = {
  intraday_guardian: '/intraday-guardian',
  daily_solver: '/daily-solver',
  creative_strategist: '/creative-strategist',
};

export async function runOptimizerTier(
  tier: OptimizerTier,
  geo: OptimizerGeo,
  options: { sync?: boolean; window_days?: number; upload_creatives?: boolean } = {}
): Promise<TierRunResult> {
  return requestJson<TierRunResult>(`${BASE}${TIER_PATHS[tier]}`, {
    method: 'POST',
    body: JSON.stringify({ geo, ...options }),
  });
}

export async function checkInvariants(
  geo: OptimizerGeo,
  actionPayload: Record<string, any>
): Promise<{ status: string; geo: OptimizerGeo; gate: { ok: boolean; violations: { gate: string; message: string }[]; checks: Record<string, any> }; pnl: Partial<MonthlyPnlSnapshot> }> {
  return requestJson(`${BASE}/invariants/check`, {
    method: 'POST',
    body: JSON.stringify({ geo, action_payload: actionPayload }),
  });
}

export async function applyOptimizerProposal(proposalId: number): Promise<{
  status: string;
  proposal_id: number;
  proposal_status: string;
  reason?: string;
  warning?: string;
  execution_result?: Record<string, any>;
}> {
  return requestJson(`${BASE}/proposals/apply`, {
    method: 'POST',
    body: JSON.stringify({ proposal_id: proposalId }),
  });
}

export async function rejectOptimizerProposal(proposalId: number): Promise<{
  status: string;
  proposal_id: number;
  proposal_status: string;
}> {
  return requestJson(`${BASE}/proposals/reject`, {
    method: 'POST',
    body: JSON.stringify({ proposal_id: proposalId }),
  });
}

export async function reopenOptimizerProposal(proposalId: number): Promise<{
  status: string;
  proposal_id: number;
  proposal_status: string;
}> {
  return requestJson(`${BASE}/proposals/reopen`, {
    method: 'POST',
    body: JSON.stringify({ proposal_id: proposalId }),
  });
}

export async function rollbackOptimizerProposal(proposalId: number): Promise<{
  status: string;
  proposal_id: number;
  proposal_status: string;
  reason?: string;
  execution_result?: Record<string, any>;
}> {
  return requestJson(`${BASE}/proposals/rollback`, {
    method: 'POST',
    body: JSON.stringify({ proposal_id: proposalId }),
  });
}

export async function applyAllSafeOptimizerProposals(
  geo: OptimizerGeo,
  includeBudgetShifts: boolean = false
): Promise<{
  status: string;
  geo: string;
  applied_count: number;
  applied_ids: number[];
  failed_ids: number[];
  blocked_ids?: number[];
}> {
  return requestJson(`${BASE}/proposals/apply-all-safe`, {
    method: 'POST',
    body: JSON.stringify({ geo, include_budget_shifts: includeBudgetShifts }),
  });
}

export interface PlatformConnectionInfo {
  platform: OptimizerPlatform;
  name: string;
  secret_name: string;
  connected: boolean;
  missing_fields: string[];
  configured_geos: string[];
  customer_ids?: Record<string, string>;
  account_ids?: Record<string, string>;
  uet_tag_ids?: Record<string, string>;
  login_customer_id?: string;
  customer_id?: string;
  developer_token_masked?: string;
  client_id_masked?: string;
  has_refresh_token?: boolean;
  builder_mode?: string;
  conversions_mode?: string;
  sync_mode?: string;
}

export interface AllConnectionsResponse {
  status: string;
  connections: {
    google: PlatformConnectionInfo;
    microsoft: PlatformConnectionInfo;
    meta: PlatformConnectionInfo;
  };
}

export async function fetchConnectionsStatus(): Promise<AllConnectionsResponse> {
  return requestJson<AllConnectionsResponse>(`${BASE}/connections`);
}

export async function savePlatformConnection(payload: Record<string, any>): Promise<{
  status: string;
  platform: string;
  connections: PlatformConnectionInfo;
}> {
  return requestJson(`${BASE}/connections`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function testPlatformConnection(
  platform: 'google' | 'microsoft',
  geo: OptimizerGeo = 'no'
): Promise<{
  status: string;
  platform: string;
  reason?: string;
  descriptive_name?: string;
  customer_id?: string;
  account_id?: string;
  note?: string;
}> {
  return requestJson(`${BASE}/connections/test`, {
    method: 'POST',
    body: JSON.stringify({ platform, geo }),
  });
}

export async function fetchOAuthAuthorizeUrl(params: {
  platform: 'google' | 'microsoft';
  redirect_uri: string;
  client_id?: string;
}): Promise<{
  status: string;
  platform: string;
  authorize_url: string;
  redirect_uri: string;
}> {
  return requestJson(`${BASE}/connections/oauth-url`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function exchangeOAuthCode(params: {
  platform: 'google' | 'microsoft';
  code: string;
  redirect_uri: string;
  client_id?: string;
  client_secret?: string;
}): Promise<{
  status: string;
  platform: string;
  has_refresh_token: boolean;
  connections: PlatformConnectionInfo;
}> {
  return requestJson(`${BASE}/connections/oauth-exchange`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}
